import { useMemo, useEffect, useCallback } from 'react';

import { useSetState } from 'src/hooks/useSetState';

import axios, { endpoints } from 'src/utils/axios';

import { LoadingScreen } from 'src/components/loadingScreen';

import { setSession } from './utils';
import { STORAGE_KEY } from './constant';
import { AuthContext } from '../auth-context';
import { ROLE_USER, type AuthState } from '../../types';

type Props = {
  children: React.ReactNode;
};
export function AuthProvider({ children }: Props) {
  const { state, setState } = useSetState<AuthState>({
    user: null,
    loading: true,
  });

  const checkUserSession = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);
      if (accessToken) {
        setSession(accessToken);
        const res = await axios.get(endpoints.auth.me);
        const { data } = res;
        setState({ user: { ...data, role: ROLE_USER[data.role], accessToken }, loading: false });
      } else {
        setState({ user: null, loading: false });
      }
    } catch (error) {
      setState({ user: null, loading: false });
    }
  }, [setState]);

  useEffect(() => {
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';
  const status = state.loading ? 'loading' : checkAuthenticated;
  const memoizedValue = useMemo(() => {
    const authenticated = !!state.user;
    return {
      user: state.user,
      checkUserSession,
      loading: status === 'loading',
      authenticated,
      unauthenticated: !authenticated,
    };
  }, [checkUserSession, state.user, status]);
  if (state.loading) {
    return <LoadingScreen />;
  }
  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
